/**
 * Single source of truth for localized URL slugs.
 *
 * English lives at the root (`/projects`), Spanish under `/es` with
 * translated segments (`/es/proyectos`). City, project and floor-plan
 * slugs stay identical in both languages (proper names that already rank).
 */

/** First-level section segments: english -> spanish. */
export const SEGMENT_EN_ES: Record<string, string> = {
  projects: "proyectos",
  "floor-plans": "planos-de-adu",
  areas: "areas-de-servicio",
  "build-your-adu": "construye-tu-adu",
  "get-started": "comenzar",
  "roi-calculator": "calculadora-de-retorno",
  events: "eventos",
  "adu-builders-san-diego": "constructores-de-adu-san-diego",
  "garage-conversion-adu-san-diego": "conversion-de-garaje-adu-san-diego",
  "detached-adu-san-diego": "adu-independiente-san-diego",
  "attached-adu-san-diego": "adu-adosada-san-diego",
  "adu-financing-san-diego": "financiamiento-de-adu-san-diego",
  visualizer: "visualizador",
  "privacy-policy": "politica-de-privacidad",
  "terms-of-use": "terminos-de-uso",
  // /recursos/glosario is already Spanish-worded in both trees (identity)
  // blog keeps the same word in both languages
};

export const SEGMENT_ES_EN: Record<string, string> = Object.fromEntries(
  Object.entries(SEGMENT_EN_ES).map(([en, es]) => [es, en]),
);

/** Blog post slugs: english -> spanish. */
export const BLOG_SLUG_EN_ES: Record<string, string> = {
  "adu-cost-san-diego-2026": "costo-de-un-adu-en-san-diego-2026",
  "adu-permit-process-san-diego": "proceso-de-permisos-adu-san-diego",
  "detached-vs-attached-adu-pros-cons": "adu-independiente-vs-adosada-ventajas",
  "adu-increase-property-value-san-diego": "adu-aumenta-el-valor-de-tu-casa-san-diego",
  "san-diego-adu-regulations-zoning-2026": "reglamentos-y-zonificacion-adu-san-diego-2026",
  "ultimate-guide-adus-san-diego": "guia-completa-de-adus-en-san-diego",
  "building-detached-adu-san-diego-teresa-story":
    "construir-un-adu-independiente-historia-de-teresa",
  "adu-garage-conversion-san-diego": "conversion-de-garaje-a-adu-san-diego",
};

export const BLOG_SLUG_ES_EN: Record<string, string> = Object.fromEntries(
  Object.entries(BLOG_SLUG_EN_ES).map(([en, es]) => [es, en]),
);

/** English blog slug for a slug coming from either language. */
export function canonicalBlogSlug(slug: string): string {
  return BLOG_SLUG_ES_EN[slug] ?? slug;
}

function splitPath(path: string): { segments: string[]; suffix: string } {
  const match = path.match(/^([^?#]*)(.*)$/);
  const clean = match ? match[1] : path;
  const suffix = match ? match[2] : "";
  return { segments: clean.split("/").filter(Boolean), suffix };
}

function joinPath(segments: string[], suffix: string): string {
  return (segments.length ? `/${segments.join("/")}` : "/") + suffix;
}

/** Translates a language-agnostic (English) path into its Spanish slugs. */
export function translateSegmentsToEs(path: string): string {
  const { segments, suffix } = splitPath(path);
  if (!segments.length) return joinPath([], suffix);

  const out = segments.map((s, i) => (i === 0 ? SEGMENT_EN_ES[s] ?? s : s));
  if (segments[0] === "blog" && segments[1]) out[1] = BLOG_SLUG_EN_ES[segments[1]] ?? segments[1];
  return joinPath(out, suffix);
}

/** Translates a Spanish path (without the /es prefix) back into English slugs. */
export function translateSegmentsToEn(path: string): string {
  const { segments, suffix } = splitPath(path);
  if (!segments.length) return joinPath([], suffix);
  const out = segments.map((s, i) => (i === 0 ? SEGMENT_ES_EN[s] ?? s : s));
  if (segments[0] === "blog" && segments[1]) out[1] = BLOG_SLUG_ES_EN[segments[1]] ?? segments[1];
  return joinPath(out, suffix);
}
